<script setup lang="ts">
type Props = {
  modelValue: boolean
  class?: string
  maxHeight?: number | string | null
  scrollableElementSelector?: string
  innerOffset?: boolean
  close?: boolean
  additional?: boolean
  overlay?: boolean
  scrollable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  class: '',
  maxHeight: null,
  scrollableElementSelector: undefined,
  overlay: true,
  innerOffset: true,
  scrollable: true,
})

const contentValue = defineModel<boolean>('contentValue', {
  required: false,
  default: true,
})

const emit = defineEmits(['update:modelValue', 'update:contentValue'])

const bottomSheetContentElement = ref<HTMLDivElement | null>(null)

const componentClasses = computed(() => {
  return [
    'app-bottom-sheet',
    props.class,
    { 'app-bottom-sheet--additional': props.additional },
  ]
})

const overlayClasses = computed(() => {
  return [
    'ui-dialog-overlay',
    { 'ui-dialog-overlay--additional': props.additional },
  ]
})

const componentStyles = computed(() => {
  return props.maxHeight ? { maxHeight: `${props.maxHeight}px` } : {}
})

const contentClasses = computed(() => {
  return [
    'app-bottom-sheet__content',
    {
      'app-bottom-sheet__content--offset': props.innerOffset,
      'app-bottom-sheet__content--scrollable': props.scrollable,
    },
  ]
})

const onClose = () => {
  emit('update:contentValue', true)
  emit('update:modelValue', false)
}

const getScrollableElement = () => {
  if (!props.scrollableElementSelector) return

  return bottomSheetContentElement.value?.querySelector(
    props.scrollableElementSelector,
  ) as HTMLElement
}

const checkScrollState = () => {
  const scrollableElement = getScrollableElement()

  if (scrollableElement) return scrollableElement.scrollTop === 0

  return bottomSheetContentElement.value?.scrollTop === 0
}

const setLockState = () => {
  if (props.additional) return

  scrollLock()
}

const setUnlockState = () => {
  if (props.additional) return

  scrollUnlock()
}

watch(
  () => props.modelValue,
  (value) => (value ? setLockState() : setUnlockState()),
)

const touchService = new TouchService({
  distance: 100,
  direction: 'down',
  onTouchEnd: () => checkScrollState() && onClose(),
})

onBeforeUnmount(() => setUnlockState())
</script>

<template>
  <Teleport to="body">
    <transition name="ui-fade">
      <template v-if="modelValue">
        <slot v-if="$slots.overlay" name="overlay" />

        <div
          v-else-if="overlay"
          :class="overlayClasses"
          data-test-id="bottom-sheet-overlay"
          @mousedown.self="onClose"
        />
      </template>
    </transition>

    <transition name="ui-slide-up">
      <div
        v-if="modelValue"
        v-show="contentValue"
        data-test-id="bottom-sheet"
        :class="componentClasses"
        :style="componentStyles"
        role="dialog"
        aria-modal="true"
        @touchstart="touchService.onStart"
        @touchmove="touchService.onMove"
        @touchend="touchService.onEnd"
      >
        <div class="app-bottom-sheet__header" role="button">
          <the-button
            v-if="close"
            data-test-id="bottom-sheet-close"
            class="app-bottom-sheet__close"
            variant="tertiary"
            size="xxs"
            radius="lg"
            icon="cancel"
            type="button"
            width="28"
            height="28"
            @click="onClose"
          />

          <div
            v-else
            data-test-id="bottom-sheet-grabber"
            class="app-bottom-sheet__grabber"
            @click="onClose"
          />

          <slot name="header" />
        </div>

        <div
          ref="bottomSheetContentElement"
          data-test-id="bottom-sheet-content"
          :class="contentClasses"
        >
          <slot />
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style lang="scss" scoped>
@use '../../assets/ui-layer/styles/mixins' as *;

.app-bottom-sheet {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 9001;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 60px);
  overflow: hidden;
  background-color: var(--ui-color-white);
  border-radius: 24px 24px 0 0;
  will-change: transform;

  &--additional {
    z-index: 9011;
  }

  @supports (height: 100dvh) {
    max-height: calc(100dvh - 30px);
  }

  &__header {
    position: relative;
    padding: 36px 20px 0;
  }

  &__grabber {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 24px;

    &::before {
      position: absolute;
      top: 8px;
      left: 50%;
      width: 35px;
      height: 5px;
      content: '';
      background-color: var(--ui-color-sandy-200);
      border-radius: 10px;
      transform: translateX(-50%);
    }
  }

  &__close {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 1000;
  }

  &__content {
    position: relative;
    overflow-y: auto;
    overscroll-behavior: none;
    -webkit-overflow-scrolling: touch;

    &--scrollable {
      @include scrollbar();
    }

    &:not(&--scrollable) {
      @include invisible-scroll-bar();
    }

    &--offset {
      padding: 0 12px;
      margin: 0 8px 32px;
    }
  }
}
</style>
